/**
 * Basemap provider registry.
 *
 * Each provider exposes the same three styles -- mapa (streets-style),
 * satelite (aerial imagery), terreno (topographic) -- so swapping the
 * active provider is a one-env-var change with no edits to MapView2D.
 *
 * Add a new provider:
 *   1. Implement `Provider | (key: string) => Provider` and add it to PROVIDERS.
 *   2. Set VITE_BASEMAP_PROVIDER=<name> in the build env.
 *
 * Default behaviour ("auto"): if VITE_MAPTILER_KEY is present, use MapTiler;
 * otherwise fall back to free public-tile servers (slow first-load on Terreno
 * because OpenTopoMap is volunteer-run, but works without any account).
 */

export type BasemapStyle = 'mapa' | 'satelite' | 'terreno';

export type BasemapEntry = {
  /** MapLibre raster tile URL template ({z}/{x}/{y}). */
  url: string;
  /** Native tile size the provider serves. Must match what MapLibre asks for. */
  tileSize: 256 | 512;
  /** HTML attribution string MapLibre shows in the corner. */
  attribution: string;
};

export type Provider = Record<BasemapStyle, BasemapEntry>;

// ---------------------------------------------------------------------------
// Public free tiles (no key, slow first-load on terrain).
// OpenStreetMap usage policy forbids "heavy commercial use" -- fine for dev
// and private beta, swap to a paid provider before public launch.
// ---------------------------------------------------------------------------
const PUBLIC_PROVIDER: Provider = {
  mapa: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    tileSize: 256,
    attribution: '&copy; OpenStreetMap contributors',
  },
  satelite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    attribution: '&copy; Esri',
  },
  terreno: {
    url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
    tileSize: 256,
    attribution: '&copy; OpenTopoMap contributors',
  },
};

// ---------------------------------------------------------------------------
// MapTiler (paid, free tier ~100k tiles/mo). Native size is 512; the /256/
// path variant returns 404. Always request 512 here.
// ---------------------------------------------------------------------------
const maptilerProvider = (key: string): Provider => ({
  mapa: {
    url: `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${key}`,
    tileSize: 512,
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
  },
  satelite: {
    url: `https://api.maptiler.com/maps/satellite-v2/{z}/{x}/{y}.jpg?key=${key}`,
    tileSize: 512,
    attribution: '&copy; MapTiler &copy; Maxar',
  },
  terreno: {
    url: `https://api.maptiler.com/maps/outdoor-v2/{z}/{x}/{y}.png?key=${key}`,
    tileSize: 512,
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
  },
});

// ---------------------------------------------------------------------------
// Self-hosted (PMTiles in our Azure Blob, served via nginx /osm-tiles/).
// Stub for now: throws if selected without first generating + uploading the
// PMTiles. Wire-in is a separate session.
// ---------------------------------------------------------------------------
const SELF_HOSTED_PROVIDER: Provider = {
  mapa: {
    url: 'pmtiles:///osm-tiles/portugal-streets.pmtiles',
    tileSize: 256,
    attribution: '&copy; OpenStreetMap contributors',
  },
  satelite: {
    // OSM has no satellite -- keep MapTiler/Mapbox/Sentinel for this.
    // Falls back to a placeholder until that decision is made.
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    attribution: '&copy; Esri',
  },
  terreno: {
    url: 'pmtiles:///osm-tiles/portugal-terrain.pmtiles',
    tileSize: 256,
    attribution: '&copy; OpenTopoMap contributors',
  },
};

// ---------------------------------------------------------------------------
// Selector. Reads VITE_BASEMAP_PROVIDER + VITE_MAPTILER_KEY at build time.
// ---------------------------------------------------------------------------
export function selectBasemapProvider(): Provider {
  const choice = (import.meta.env.VITE_BASEMAP_PROVIDER || 'auto') as string;
  const maptilerKey = import.meta.env.VITE_MAPTILER_KEY as string | undefined;

  if (choice === 'public') return PUBLIC_PROVIDER;
  if (choice === 'self') return SELF_HOSTED_PROVIDER;
  if (choice === 'maptiler') {
    if (!maptilerKey) {
      console.warn('VITE_BASEMAP_PROVIDER=maptiler but VITE_MAPTILER_KEY is unset; falling back to public');
      return PUBLIC_PROVIDER;
    }
    return maptilerProvider(maptilerKey);
  }
  // 'auto' (default): MapTiler when keyed, public otherwise.
  if (maptilerKey) return maptilerProvider(maptilerKey);
  return PUBLIC_PROVIDER;
}
